import React, { useCallback, useState, useMemo } from 'react'

import PropTypes from 'prop-types'

import map from 'lodash/map'
import toLower from 'lodash/toLower'
import uniqBy from 'lodash/uniqBy'

import { useT } from '@galo-monitor/i18n'
import { attachModuleApplication, detachModuleApplication } from '@galo-monitor/services/apis/galoMonitorApi/resources/module'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import Modal from '@galo-monitor/web-components/Modal'
import TransferList from '@galo-monitor/web-components/TransferList'

const ModuleApplicationsModal = (props) => {
  const {
    id,
    open,
    mod
  } = props

  const [loading, setLoading] = useState(false)

  const snackbar = useSnackbar()

  const { data: apps, mutate: appsMutate, isValidating: appsIsValidating } = useGaloMonitorApi(`/admin/functionalities/available/${ mod.contextSlug }`)
  const { data: moduleApps, mutate: moduleAppsMutate, isValidating: moduleIsValidating } = useGaloMonitorApi(`/admin/functionalities/enabled/${ mod.id }`)

  const applicationsOptions = useMemo(
    () => {
      const allApps = uniqBy([
        ...(apps || []),
        ...(moduleApps || [])
      ], 'functionalityId')
      return map(allApps, app => ({ value: app.functionalityId, label: app.name }))
    },
    [apps, moduleApps]
  )

  const value = useMemo(
    () => map(moduleApps, app => app.functionalityId),
    [moduleApps]
  )

  const t = useT()

  const movedToLeft = useCallback(
    async (items) => {
      try {
        setLoading(true)
        const functionalities = map(items, item => item.value.toString())
        await detachModuleApplication({ functionalities })

        snackbar.success(t('{this} {action} successfully!', {
          this: t('permission', { howMany: items.length }),
          action: toLower(t('detach', { gender: 'female', howMany: items.length }))
        }))

        await Promise.all([
          moduleAppsMutate(),
          appsMutate()
        ])
      } finally {
        setLoading(false)
      }
    },
    [appsMutate, moduleAppsMutate, snackbar, t]
  )

  const movedToRight = useCallback(
    async (items) => {
      try {
        setLoading(true)
        const functionalities = map(items, item => item.value.toString())
        await attachModuleApplication({ functionalities, moduleId: mod.id })

        snackbar.success(t('{this} {action} successfully!', {
          this: t('permission', { howMany: items.length }),
          action: toLower(t('attach', { gender: 'female', howMany: items.length }))
        }))

        await Promise.all([
          moduleAppsMutate(),
          appsMutate()
        ])
      } finally {
        setLoading(false)
      }
    },
    [appsMutate, mod.id, moduleAppsMutate, snackbar, t]
  )

  return (
    <Modal
      id={ id }
      open={ open }
      loading={ loading }
      title={ t('managing {this} permissions', { this: t('module', { howMany: 1 }) }) }
      subTitle={ `${ mod.name } (${ mod.contextName })` }
      maxWidth="md"
      fullWidth
    >
      <TransferList
        detached
        name="functionalities"
        labelLeft={ t('permission', { howMany: 2 }) }
        labelRight={ t('module permissions') }
        options={ applicationsOptions }
        value={ value }
        loading={ appsIsValidating || moduleIsValidating }
        movedToLeft={ movedToLeft }
        movedToRight={ movedToRight }
      />
    </Modal>
  )
}

ModuleApplicationsModal.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  mod: PropTypes.object
}

ModuleApplicationsModal.defaultProps = {
  mod: {}
}

export default ModuleApplicationsModal
